
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useSettings } from "@/providers/SettingsProvider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { addMinutes } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { RelationPicker } from "./RelationPicker";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CreateEventModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (eventData: { 
		title: string; 
		start: Date; 
		end: Date; 
		description?: string; 
		color?: string;
		relations?: {
			candidates: string[];
			offers: string[];
			companies: string[];
			matches: string[];
			contacts: string[];
		};
	}) => void;
	initialDate?: Date;
}

const EVENT_COLORS = [
	{ value: "#9b87f5", label: "Purple" },
	{ value: "#1EAEDB", label: "Blue" },
	{ value: "#F97316", label: "Orange" },
	{ value: "#D946EF", label: "Pink" },
	{ value: "#0EA5E9", label: "Sky" },
	{ value: "#8E9196", label: "Gray" },
];

export const CreateEventModal = ({ isOpen, onClose, onSave, initialDate }: CreateEventModalProps) => {
	const { calendarSettings, timezone } = useSettings();
	const defaultDuration = calendarSettings?.defaultEventDuration || 60;
	const defaultColor = calendarSettings?.defaultEventColor || "#9b87f5";
	const userTimezone = timezone || 'UTC';
	
	const [title, setTitle] = useState("");
	const [description, setDescription] = useState("");
	const [color, setColor] = useState(defaultColor);
	const [startDate, setStartDate] = useState<Date>(initialDate || new Date());
	const [endDate, setEndDate] = useState<Date>(
		addMinutes(initialDate || new Date(), defaultDuration)
	);
	
	const [selectedRelations, setSelectedRelations] = useState({
		candidates: [] as string[],
		offers: [] as string[],
		companies: [] as string[],
		matches: [] as string[],
		contacts: [] as string[],
	});

	useEffect(() => {
		if (isOpen) {
			setTitle("");
			setDescription("");
			const start = initialDate ? new Date(initialDate) : new Date();
			if (!isNaN(start.getTime())) {
				setStartDate(start);
				setEndDate(addMinutes(start, defaultDuration));
			} else {
				const currentTime = new Date();
				setStartDate(currentTime);
				setEndDate(addMinutes(currentTime, defaultDuration));
			}
			setColor(defaultColor);
			setSelectedRelations({
				candidates: [],
				offers: [],
				companies: [],
				matches: [],
				contacts: [],
			});
		}
	}, [isOpen, initialDate, defaultDuration, defaultColor]);

	const handleRelationSelect = (type: string, id: string) => {
		setSelectedRelations(prev => ({
			...prev,
			[type]: [...prev[type], id]
		}));
	};

	const handleRelationRemove = (type: string, id: string) => {
		setSelectedRelations(prev => ({
			...prev,
			[type]: prev[type].filter(itemId => itemId !== id)
		}));
	};

	const formatDateForInput = (date: Date) => {
		try {
			return formatInTimeZone(date, userTimezone, "yyyy-MM-dd'T'HH:mm");
		} catch (error) {
			console.error("Error formatting date:", error);
			return date.toISOString().slice(0, 16);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		
		try {
			if (!title.trim()) {
				toast.error("Event title is required");
				return;
			}
			
			if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
				toast.error("Invalid date format");
				return;
			}
			
			if (endDate <= startDate) {
				toast.error("End time must be after start time");
				return;
			}
			
			onSave({
				title,
				start: startDate,
				end: endDate,
				description: description || undefined,
				color,
				relations: selectedRelations
			});
			
			onClose();
		} catch (error) {
			console.error("Error creating event:", error);
			toast.error("Error creating event. Please check your input.");
		}
	};

	const handleStartDateChange = (dateString: string) => {
		try {
			const newStartDate = new Date(dateString);
			if (!isNaN(newStartDate.getTime())) {
				setStartDate(newStartDate);
				const newEndDate = addMinutes(newStartDate, defaultDuration);
				setEndDate(newEndDate);
			}
		} catch (error) {
			console.error("Error updating dates:", error);
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-[900px] w-[90vw] max-h-[80vh] overflow-hidden">
				<DialogHeader>
					<DialogTitle>Create Event</DialogTitle>
				</DialogHeader>
				
				<div className="grid grid-cols-2 gap-6 overflow-hidden">
					{/* Left side - Event details */}
					<ScrollArea className="h-[calc(80vh-180px)] pr-4">
						<form onSubmit={handleSubmit} className="space-y-4">
							<div>
								<Label htmlFor="title">Title</Label>
								<Input
									id="title"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									required
								/>
							</div>
							
							<div className="grid grid-cols-2 gap-4">
								<div>
									<Label htmlFor="start">Start ({userTimezone})</Label>
									<Input
										id="start"
										type="datetime-local"
										value={formatDateForInput(startDate)}
										onChange={(e) => handleStartDateChange(e.target.value)}
									/>
								</div>
								
								<div>
									<Label htmlFor="end">End ({userTimezone})</Label>
									<Input
										id="end"
										type="datetime-local"
										value={formatDateForInput(endDate)}
										onChange={(e) => setEndDate(new Date(e.target.value))}
									/>
								</div>
							</div>

							<div>
								<Label htmlFor="color">Color</Label>
								<Select value={color} onValueChange={setColor}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{EVENT_COLORS.map((color) => (
											<SelectItem key={color.value} value={color.value}>
												<div className="flex items-center gap-2">
													<div 
														className="w-4 h-4 rounded-full" 
														style={{ backgroundColor: color.value }}
													/>
													{color.label}
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label htmlFor="description">Description</Label>
								<Textarea
									id="description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									className="h-32"
								/>
							</div>
						</form>
					</ScrollArea>

					{/* Right side - Relation picker */}
					<div className="border-l pl-6 h-[calc(80vh-180px)] overflow-hidden">
						<Label className="mb-2 block">Related Items</Label>
						<RelationPicker
							selectedRelations={selectedRelations}
							onSelect={handleRelationSelect}
							onRemove={handleRelationRemove}
						/>
					</div>
				</div>

				<div className="flex justify-end gap-2 mt-2">
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button onClick={handleSubmit}>
						Create Event
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};
